import { useState, useEffect, useCallback } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../services/apiClient';
import { projectService } from '../services/projectService';
import { ProjectItem, ProjectType } from '../types/project';

interface UseProjectsReturn {
  projects: ProjectItem[];
  loading: boolean;
  error: string;
  fetchProjects: () => Promise<void>;
  fetchProject: (_projectId: number) => Promise<ProjectItem>;
  createProject: (_title: string) => Promise<ProjectItem>;
  updateProjectTitle: (_projectId: number, _title: string) => Promise<ProjectItem>;
  publishProject: (_projectId: number) => Promise<ProjectItem>;
  deleteProject: (_projectId: number) => Promise<void>;
  clearError: () => void;
}

export const useProjects = (): UseProjectsReturn => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  /**
   * Fetch projects from the API
   */
  const fetchProjects = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    
    try {
      const result = await projectService.getProjects();
      setProjects(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`API Error: ${err.message}`);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch a single project by ID
   * @param projectId - The ID of the project to fetch
   */
  const fetchProject = useCallback(async (projectId: number): Promise<ProjectItem> => {
    setError('');
    
    try {
      const result = await projectService.getProject(projectId);
      return result;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Fetch Project Error: ${err.message}`);
      } else {
        setError('Failed to fetch project');
      }
      throw err; // Re-throw to allow component to handle it
    }
  }, []);

  /**
   * Create a new project and refresh the list
   * @param title - The title of the project to create
   */
  const createProject = useCallback(async (title: string): Promise<ProjectItem> => {
    setError('');
    
    try {
      const projectData = {
        title,
        description: 'New project description',
        projectType: ProjectType.GREEN_FIELD,
      };
      const result = await projectService.createProject(projectData);
      // Refresh the projects list after successful creation
      await fetchProjects();
      return result;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Create Project Error: ${err.message}`);
      } else {
        setError('Failed to create project');
      }
      throw err; // Re-throw to allow component to handle it
    }
  }, [fetchProjects]);

  /**
   * Update a project's title and refresh the list
   * @param projectId - The ID of the project to update
   * @param title - The new title for the project
   */
  const updateProjectTitle = useCallback(
    async (projectId: number, title: string): Promise<ProjectItem> => {
      setError('');
      
      try {
        const result = await projectService.updateProjectTitle(projectId, title);
        return result;
      } catch (err) {
        if (err instanceof ApiError) {
          setError(`Update Project Error: ${err.message}`);
        } else {
          setError('Failed to update project');
        }
        throw err; // Re-throw to allow component to handle it
      }
    }, []);

  /**
   * Publish a project and refresh the list.
   * @param projectId - The ID of the project to publish
   */
  const publishProject = useCallback(
    async (projectId: number): Promise<ProjectItem> => {
      setError('');

      try {
        const result = await projectService.publishProject(projectId);
        await fetchProjects();
        return result;
      } catch (err) {
        if (err instanceof ApiError) {
          setError(`Publish Error: ${err.message}`);
        } else {
          setError('Failed to publish project');
        }
        throw err;
      }
    },
    [fetchProjects],
  );

  /**
   * Delete a project by ID and refresh the list
   * @param projectId - The ID of the project to delete
   */
  const deleteProject = useCallback(async (projectId: number): Promise<void> => {
    setError('');
    
    try {
      await projectService.deleteProject(projectId);
      // Refresh the projects list after successful deletion
      await fetchProjects();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Delete Error: ${err.message}`);
      } else {
        setError('Failed to delete project');
      }
      throw err; // Re-throw to allow component to handle it
    }
  }, [fetchProjects]);

  /**
   * Clear error state
   */
  const clearError = useCallback((): void => {
    setError('');
  }, []);

  // Load projects on mount - BUT ONLY after auth has finished loading
  useEffect(() => {
    // Wait for auth to finish loading before fetching projects
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      return;
    }
    
    fetchProjects();
  }, [fetchProjects, authLoading, isAuthenticated]);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    fetchProject,
    createProject,
    updateProjectTitle,
    publishProject,
    deleteProject,
    clearError,
  };
};
